import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const SECTIONS = [
  {
    heading: "1. Who we are",
    body: [
      "The Syndicate is a private trading community operated by Issy Flash (\"we\", \"us\", \"our\"). This Privacy Policy explains what data we collect through thesyndicate.vip and how we use it.",
    ],
  },
  {
    heading: "2. Data we collect",
    body: [
      "The Syndicate marketing site itself does not collect personal information. We do not run our own signup, login, or database of customers.",
      "When you click JOIN and check out, you are handed off to Whop (whop.com) — a third-party payment and community platform. Whop collects the information necessary to process your purchase (name, email, billing details, payment token). That information is governed by Whop's privacy policy, not ours.",
      "We may collect anonymous browsing analytics (pages viewed, device type, referrer) to improve the site. No personally identifying data is stored.",
    ],
  },
  {
    heading: "3. Payments",
    body: [
      "All payments are processed by Whop. We never see, store, or handle your card number, CVV or bank details. Whop is a PCI-DSS compliant payment platform.",
    ],
  },
  {
    heading: "4. Community access",
    body: [
      "After a successful payment, Whop delivers access to the paid Telegram group directly. You will only ever be added to the group you paid for. If you cancel or refund, Whop removes your access automatically at the end of your billing period.",
    ],
  },
  {
    heading: "5. Cookies",
    body: [
      "We use essential cookies to keep the site functional and, where enabled, anonymous analytics cookies. You can disable cookies in your browser settings — the site will still work.",
    ],
  },
  {
    heading: "6. Third parties",
    body: [
      "The following third-party services may receive data when you interact with our site or checkout:",
      "• Whop — payments, community access, subscription management (whop.com/privacy).",
      "• Telegram — community delivery (their privacy policy applies once you join).",
      "• Analytics providers — anonymous traffic data only.",
    ],
  },
  {
    heading: "7. Data retention & deletion",
    body: [
      "Because we do not store personal data on our own systems, there is nothing to delete on our side. To remove your data from Whop or Telegram, use those platforms' account settings.",
    ],
  },
  {
    heading: "8. Your rights",
    body: [
      "Depending on your jurisdiction (UK/EU GDPR, California CCPA, etc.) you may have rights to access, correct, or erase personal data held about you. For data held by Whop, follow the procedure in Whop's privacy policy. For anything held by us, contact @issyflash on Telegram.",
    ],
  },
  {
    heading: "9. Content is not financial advice",
    body: [
      "Nothing shared on this site or inside our private groups constitutes financial, investment, tax or legal advice. All trading involves risk, including full loss of capital. You are solely responsible for your own decisions.",
    ],
  },
  {
    heading: "10. Changes to this policy",
    body: [
      "We may update this Privacy Policy from time to time. When we do, we'll update the effective date below. Please review it periodically.",
    ],
  },
  {
    heading: "11. Contact",
    body: [
      "Questions about this policy? DM Issy Flash directly on Telegram at @issyflash, or reach out on Instagram / X / TikTok (all @issyflash).",
    ],
  },
];

export default function Privacy() {
  return (
    <div className="relative min-h-screen bg-black text-white overflow-x-hidden grain" data-testid="privacy-page">
      <Navbar />

      <section className="relative pt-36 md:pt-44 pb-12 px-4 md:px-6">
        <div className="absolute inset-0 dot-pattern opacity-30 pointer-events-none" />
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <p className="font-mono-serif text-[10px] tracking-[0.4em] text-white/45">
            LEGAL // PRIVACY POLICY
          </p>
          <h1 className="mt-4 font-display text-5xl md:text-7xl tracking-tight">
            Privacy Policy
          </h1>
          <p className="mt-5 font-mono-serif text-[10px] tracking-[0.3em] text-white/45">
            EFFECTIVE {new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" }).toUpperCase()}
          </p>
        </div>
      </section>

      <section className="relative z-10 px-4 md:px-6 pb-20">
        <div className="max-w-3xl mx-auto frame p-6 md:p-10 space-y-8 md:space-y-10">
          {SECTIONS.map((s, i) => (
            <div key={s.heading} data-testid={`privacy-section-${i}`}>
              <h2 className="font-display text-2xl md:text-3xl">{s.heading}</h2>
              <div className="mt-3 space-y-3 text-white/70 text-base md:text-[16px] leading-relaxed">
                {s.body.map((p, j) => (
                  <p key={j}>{p}</p>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="relative z-10 px-4 md:px-6 pb-24">
        <div className="max-w-3xl mx-auto text-center frame p-10 md:p-14">
          <h2 className="font-display text-3xl md:text-4xl">Any questions?</h2>
          <p className="mt-4 text-white/60">
            Reach out on Telegram — we reply quickly.
          </p>
          <a
            href="https://t.me/issyflash"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="privacy-contact-btn"
            className="mt-8 inline-flex items-center gap-3 btn-shimmer text-black font-mono-serif text-xs tracking-[0.3em] px-8 py-4 rounded-full hover:opacity-95 transition"
          >
            DM @ISSYFLASH <ArrowRight size={16} />
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
